import React, { useState, useEffect } from 'react';
import { useT } from '../../../i18n';
import { FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';

/**
 * Componente de paginação para o histórico de apostas
 */
const HistoryPagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
  const t = useT();
  const [pageCount, setPageCount] = useState(1);
  const [visiblePages, setVisiblePages] = useState([]);
  
  // Calcular número total de páginas quando os itens mudam
  useEffect(() => {
    const count = Math.ceil(totalItems / itemsPerPage);
    setPageCount(count || 1);
  }, [totalItems, itemsPerPage]);
  
  // Determinar quais números de página mostrar
  useEffect(() => {
    const getVisiblePages = () => {
      // Se tiver 7 ou menos páginas, mostrar todas
      if (pageCount <= 7) {
        return Array.from({ length: pageCount }, (_, i) => i + 1);
      }
      
      // Caso contrário, mostrar páginas ao redor da atual com elipses
      let pages = [];
      
      // Sempre mostrar primeira página
      pages.push(1);
      
      // Determinar início e fim das páginas visíveis
      if (currentPage <= 3) {
        // Próximo da primeira página
        pages = [...pages, 2, 3, 4, 5, '...', pageCount];
      } else if (currentPage >= pageCount - 2) {
        // Próximo da última página
        pages = [...pages, '...', pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount];
      } else {
        // No meio
        pages = [...pages, '...', currentPage - 1, currentPage, currentPage + 1, '...', pageCount];
      }
      
      return pages;
    };
    
    setVisiblePages(getVisiblePages());
  }, [currentPage, pageCount]);
  
  // Manipuladores de navegação
  const handlePageClick = (page) => {
    if (page !== '...' && page !== currentPage) {
      onPageChange(page);
    }
  };
  
  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };
  
  const handleNext = () => {
    if (currentPage < pageCount) {
      onPageChange(currentPage + 1);
    }
  };
  
  const handleFirst = () => {
    if (currentPage !== 1) {
      onPageChange(1);
    }
  };
  
  const handleLast = () => {
    if (currentPage !== pageCount) {
      onPageChange(pageCount);
    }
  };
  
  // Não mostrar paginação se houver apenas uma página
  if (pageCount <= 1) return null;
  
  return (
    <div className="flex justify-center items-center space-x-1 mt-4">
      {/* Botão para primeira página */}
      <button
        onClick={handleFirst}
        disabled={currentPage === 1}
        className={`p-2 rounded-full ${
          currentPage === 1
            ? 'text-gray-500 cursor-not-allowed'
            : 'text-brand hover:bg-brand/20'
        }`}
        aria-label={t('bvb.firstPage')}
      >
        <FaAngleDoubleLeft size={14} />
      </button>
      
      {/* Botão anterior */}
      <button
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className={`p-2 rounded-full ${
          currentPage === 1
            ? 'text-gray-500 cursor-not-allowed'
            : 'text-brand hover:bg-brand/20'
        }`}
        aria-label={t('bvb.prevPage')}
      >
        <FaChevronLeft size={14} />
      </button>
      
      {/* Números de página */}
      {visiblePages.map((page, index) => (
        <button
          key={`page-${index}`}
          onClick={() => handlePageClick(page)}
          disabled={page === '...'}
          className={`w-8 h-8 flex items-center justify-center rounded-full ${
            page === currentPage
              ? 'bg-brand text-white'
              : page === '...'
                ? 'text-gray-500 cursor-default'
                : 'text-white hover:bg-brand/20'
          }`}
        >
          {page}
        </button>
      ))}
      
      {/* Botão próximo */}
      <button
        onClick={handleNext}
        disabled={currentPage === pageCount}
        className={`p-2 rounded-full ${
          currentPage === pageCount
            ? 'text-gray-500 cursor-not-allowed'
            : 'text-brand hover:bg-brand/20'
        }`}
        aria-label={t('bvb.nextPage')}
      >
        <FaChevronRight size={14} />
      </button>
      
      {/* Botão para última página */}
      <button
        onClick={handleLast}
        disabled={currentPage === pageCount}
        className={`p-2 rounded-full ${
          currentPage === pageCount
            ? 'text-gray-500 cursor-not-allowed'
            : 'text-brand hover:bg-brand/20'
        }`}
        aria-label={t('bvb.lastPage')}
      >
        <FaAngleDoubleRight size={14} />
      </button>
    </div>
  );
};

export default HistoryPagination;
